<template>
  <div :class="$style.container">
    <!-- タイトルエリア -->
    <div :class="$style.titleArea">
      <div :class="$style.titleArea__status">
        <StateChip
          :text="statusString(calendar.status)"
          :variation="statusVariation(calendar.status)"
        />
      </div>
      <div :class="$style.titleArea__content">
        <h2 :class="$style.title">{{ calendar.title }}</h2>
        <p :class="$style.description" v-if="calendar.description">
          {{ calendar.description }}
        </p>
      </div>
    </div>

    <!-- 基本情報（横並び） -->
    <div :class="$style.infoSection">
      <div :class="$style.infoCard">
        <p :class="$style.infoCard__title">1枠の所要時間</p>
        <div :class="$style.infoCard__content">
          {{ calendar.durationMinutes }}分
        </div>
      </div>

      <div :class="$style.infoCard">
        <p :class="$style.infoCard__title">枠の表示間隔</p>
        <div :class="$style.infoCard__content">
          <span>{{ calendar.offsetMinutes }}分</span>
        </div>
      </div>

      <div :class="[$style.infoCard, $style.infoCard__wide]">
        <p :class="$style.infoCard__title">カレンダーメンバー</p>
        <div :class="$style.infoCard__content">
          <div :class="$style.staffBadgeContainer">
            <div
              v-for="staff in assignedStaffs"
              :key="staff.guid"
              :class="$style.staffBadge"
            >
              {{ staff.displayName }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="$style.container__block">
      <p :class="$style.container__title">表示する時間範囲</p>
      <div :class="$style.availabilityCards">
        <div
          v-for="day in weekdayNames.keys()"
          :key="day"
          :class="$style.dayCard"
        >
          <div :class="$style.dayCard__header">
            {{ weekdayNames.get(day) }}
          </div>
          <div :class="$style.dayCard__body">
            <template v-if="getDayAvailabilities(day).length > 0">
              <div
                v-for="availability in getDayAvailabilities(day).slice(
                  0,
                  displayLimitForDayAvailability
                )"
                :key="`${availability.dayOfWeek}-${formatTime(availability.from)}-${formatTime(availability.until)}`"
              >
                {{ formatTime(availability.from) }} -
                {{ formatTime(availability.until) }}
              </div>
              <div
                v-if="
                  getDayAvailabilities(day).length >
                  displayLimitForDayAvailability
                "
                :class="$style.dayCard__more"
              >
                +{{
                  getDayAvailabilities(day).length -
                  displayLimitForDayAvailability
                }}件
              </div>
            </template>
            <template v-else> 未設定 </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import StateChip from '@/components/atoms/StateChip.vue'
import type { FormType } from './formSchema'
import {
  weekdayNames,
  statusString,
  statusVariation,
  formatTime,
  type StaffType,
} from './calendar.ts'
import type { DayOfWeek } from '@/types/schema.generated.ts'

const props = defineProps<{
  calendar: FormType
  staffs: StaffType[]
}>()

const assignedStaffs = computed<StaffType[]>(() =>
  props.calendar.assignedStaffGUIDs
    .map(staffGUID => props.staffs.find(staff => staff.guid === staffGUID))
    .filter(staff => staff !== undefined)
)

// Get availabilities for a specific day
const getDayAvailabilities = (day: DayOfWeek) => {
  // Filter availabilities for the specified day and sort them by from time
  return props.calendar.dailyAvailabilities.filter(
    availability => availability.dayOfWeek === day
  )
}

const displayLimitForDayAvailability = 3
</script>

<style lang="scss" module>
.container {
  &__block {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: bold;
    color: $color-text-and-object-medium-emphasis;
    margin-bottom: 16px;
  }
}

.content {
  font-size: 16px;
  color: $color-text-and-object-high-emphasis;
  line-height: 1.5;
}

.staffBadgeContainer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.staffBadge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: $color-surface-secondary;
  border-radius: $border-radius-v2-base;
  color: $color-text-and-object-high-emphasis;
  font-size: 14px;
}

.descriptionBox {
  padding: 16px;
  background-color: $color-surface-secondary;
  border-radius: $border-radius-v2-base;
  white-space: pre-wrap;
  line-height: 1.6;
}

.titleArea {
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;

  &__status {
    margin-right: 16px;
    padding-top: 4px;
  }

  &__content {
    flex: 1;
  }
}

.title {
  font-size: 24px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis;
  margin: 0;
  line-height: 1.3;
}

.description {
  margin-top: 8px;
  font-size: 16px;
  color: $color-text-and-object-medium-emphasis;
  line-height: 1.5;
}

.availabilityCards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.dayCard {
  min-width: 124px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  overflow: hidden;

  &__header {
    background-color: $color-gray-100;
    color: $color-text-and-object-medium-emphasis;
    font-weight: bold;
    text-align: center;
    padding: 8px;
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  &__body {
    padding: 12px;
    font-size: 14px;
    color: $color-text-and-object-high-emphasis;
    min-height: 60px;
    text-align: center;

    > div {
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__more {
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;
    text-align: right;
    margin-top: 4px;
  }
}

// 横並び情報カード
.infoSection {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 40px;
}

.infoCard {
  flex: 1;
  min-width: 200px;

  &__wide {
    flex: 2;
    min-width: 300px;
  }

  &__title {
    font-size: 14px;
    font-weight: bold;
    color: $color-text-and-object-medium-emphasis;
    margin-bottom: 8px;
  }

  &__content {
    font-size: 16px;
    color: $color-text-and-object-high-emphasis;
    line-height: 1.5;
  }
}
</style>
